import React, {
  useState, useEffect, useMemo,
} from "react"
import pluralize from "pluralize"
import {
  FormControl,
  FormLabel,
  Select as ChakraSelect,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useHistory } from "react-router-dom"
import { useCurrentEvent } from "queries/events"
import { useCurrentUser } from "queries/users"
import useGuestsStore from "services/useGuestsStore"
import { useShallow } from "zustand/shallow"
import { useBadges } from "queries/badges"
import { useGuests } from "queries/guests"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import { errorToast, successToast } from "utilities/toasts"
import { get } from "services/api"
import { getBadgeExportUrl } from "api/badges"
import { authToken } from "services/authentication"
import { downloadFileFromUrl } from "utilities/fileDownload"
import { Guest, SubscriptionType } from "sharedTypes"
import { GuestData } from "components/BadgeBuilder/utils/badgeDataResolver"
import useFeatures from "services/useFeatures"
import UpgradeAlert from "components/elements/UpgradeAlert"
import { useEventGuestPaths } from "utilities/routes"
import { useUrlFilters } from "utilities/filterRulesUrl"
import DocsLink from "components/DocsLink"
import { DESKTOP_PDF_BADGE_PRINTING_ARTICLE_URL } from "utilities/externalLinks"
import { guestFullName } from "utilities/strings"
import {
  ExportFormat, AVERY_FORMAT_CATEGORIES, getSheetForFormat, calculateSheetGrid,
} from "components/BadgeBuilder/utils/sheetPresets"
import NoBadges from "./NoBadges"
import SheetFormatSelector from "./SheetFormatSelector"
import CustomSheetModal from "./CustomSheetModal"
import usePrintBadgeSettings from "./usePrintBadgeSettings"

type Props = {
  onClose: () => void
  selectedCount?: number
  guest?: Guest
}

type ViewState = "main" | "customSheet"

const DPI_OPTIONS = [
  { label: "72", value: 72 },
  { label: "96", value: 96 },
  { label: "150", value: 150 },
  { label: "200", value: 200 },
  { label: "203", value: 203 },
  { label: "240", value: 240 },
  { label: "300", value: 300 },
]

const FORMAT_LABELS: Partial<Record<ExportFormat, string>> = {
  [ExportFormat.AVERY_5392]: `-${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5392].displayName}-${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5392].formatLabel}`,
  [ExportFormat.AVERY_5390]: `-${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5390].displayName}-${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_5390].formatLabel}`,
  [ExportFormat.AVERY_8395]: `-${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_8395].displayName}-${AVERY_FORMAT_CATEGORIES[ExportFormat.AVERY_8395].formatLabel}`,
  [ExportFormat.CUSTOM_SHEET]: "-Custom Layout",
}

const PrintBadge = ({
  onClose, selectedCount, guest,
}: Props) => {
  const { data: currentEvent } = useCurrentEvent()
  const currentUser = useCurrentUser()
  const { badgePrinting } = useFeatures()
  const history = useHistory()
  const { eventGuestPath } = useEventGuestPaths()
  const eventId = guest?.eventId || currentEvent.id
  const { data: badgesResponse, isLoading } = useBadges(eventId)
  const {
    selectedGuestIds,
    deselectAll,
    allSelected,
    groupId,
    segmentId,
    searchString,
    order,
  } = useGuestsStore(
    useShallow((state) => ({
      selectedGuestIds: state.selectedGuestIds,
      deselectAll: state.deselectAll,
      allSelected: state.allSelected,
      groupId: state.groupId,
      segmentId: state.segmentId,
      searchString: state.searchString,
      order: state.order,
    })),
  )
  const filters = useUrlFilters()

  // In bulk mode, tap into the already-cached guest list for preview data
  const { guests: cachedGuests } = useGuests(
    eventId, searchString, order, groupId, segmentId, filters,
  )

  const badges = badgesResponse?.data || []

  const {
    preferredBadgeId, setPreferredBadgeId,
    dpi, setDpi,
    selectedExportFormat, setSelectedExportFormat,
    customSheet, setCustomSheet,
  } = usePrintBadgeSettings(eventId)

  const [isDownloading, setIsDownloading] = useState(false)
  const [startPosition, setStartPosition] = useState<number>(1)
  const [view, setView] = useState<ViewState>("main")

  // If the stored badge no longer exists in the list, fall back to the first one
  useEffect(() => {
    if (badges.length === 0) return
    if (preferredBadgeId && badges.some((b) => b.id === preferredBadgeId)) return
    setPreferredBadgeId(badges[0].id)
  }, [badges, preferredBadgeId])

  const selectedBadge = badges.find((b) => b.id === preferredBadgeId)

  // Build preview guests array: single-guest mode uses the guest prop,
  // bulk mode uses the cached guest list (GuestSummary[]).
  // Both Guest and GuestSummary satisfy GuestData (personInfo + seatingAssignment + eventGroups).
  const previewGuests: GuestData[] = useMemo(() => {
    if (guest) return [guest]
    if (allSelected) return cachedGuests
    const selectedSet = new Set(selectedGuestIds)

    return cachedGuests.filter((g) => selectedSet.has(g.id))
  }, [guest, allSelected, cachedGuests, selectedGuestIds])

  const singleGuest = guest || (previewGuests.length === 1 ? previewGuests[0] : undefined)
  const guestName = singleGuest
    ? guestFullName(singleGuest.personInfo) || "Guest"
    : ""

  const isSheetMode = selectedExportFormat !== ExportFormat.SINGLE
  const resolvedSheet = selectedExportFormat === ExportFormat.CUSTOM_SHEET
    ? customSheet
    : getSheetForFormat(selectedExportFormat)
  const resolvedGrid = resolvedSheet ? calculateSheetGrid(resolvedSheet) : null
  const totalLabels = resolvedGrid ? resolvedGrid.columns * resolvedGrid.rows : 0

  const shouldShowStartPosition = isSheetMode && totalLabels > 1

  const isCustomSheet = selectedExportFormat === ExportFormat.CUSTOM_SHEET
  const isCustomSheetNotConfigured = isCustomSheet && !customSheet

  // Clamp startPosition when totalLabels changes (e.g., after editing custom sheet)
  useEffect(() => {
    if (startPosition > totalLabels && totalLabels > 0) {
      setStartPosition(1)
    }
  }, [totalLabels, startPosition])

  const downloadSingleBadge = async (guestId: string) => {
    if (!preferredBadgeId) return
    setIsDownloading(true)

    const authHeaders = { headers: { Authorization: `Token token="${authToken()}"` } }
    const url = getBadgeExportUrl(
      eventId, preferredBadgeId, [guestId], undefined, undefined, dpi,
      isSheetMode ? selectedExportFormat : undefined,
      isSheetMode ? startPosition : undefined,
      isCustomSheet ? resolvedSheet ?? undefined : undefined,
    )
    const formatLabel = isSheetMode ? (FORMAT_LABELS[selectedExportFormat] ?? "") : ""
    const filename = `Diobox ${currentEvent.title}-${selectedBadge?.name}-${guestName}${formatLabel}.pdf`.replace(/[^a-z0-9-_.]/gi, "-")

    try {
      await downloadFileFromUrl(url, filename, authHeaders)
      successToast({ title: "Badge downloaded successfully." })
      onClose()
      if (guest) {
        history.push(eventGuestPath(eventId, guest.id))
      } else {
        deselectAll()
      }
    } catch {
      errorToast({ title: "Failed to download badge." })
    } finally {
      setIsDownloading(false)
    }
  }

  const exportBulkBadges = async () => {
    if (!preferredBadgeId) return
    setIsDownloading(true)

    const url = getBadgeExportUrl(
      eventId,
      preferredBadgeId,
      allSelected ? undefined : selectedGuestIds,
      allSelected ? segmentId : undefined,
      allSelected ? (groupId || "all") : undefined,
      dpi,
      isSheetMode ? selectedExportFormat : undefined,
      isSheetMode ? startPosition : undefined,
      isCustomSheet ? resolvedSheet ?? undefined : undefined,
    )

    try {
      await get(url)

      const count = selectedCount || 0
      successToast({
        title: "Badge export in progress",
        description: `A PDF file with ${count} ${pluralize("badge", count)} will be emailed to ${currentUser.email}`,
      })

      deselectAll()
      onClose()
    } finally {
      setIsDownloading(false)
    }
  }

  const onSubmit = async () => {
    const isSingleGuest = guest || (!allSelected && selectedGuestIds.length === 1)
    if (isSingleGuest) {
      await downloadSingleBadge(guest?.id || selectedGuestIds[0])
    } else {
      await exportBulkBadges()
    }
  }

  const count = selectedCount || 0
  const submitText = guest
    ? "Download Badge"
    : `Export ${count} ${pluralize("Badge", count)}`

  const hasNoBadges = !isLoading && badges.length === 0

  if (view === "customSheet") {
    return (
      <CustomSheetModal
        isOpen
        onClose={() => setView("main")}
        onSave={(sheet) => {
          setCustomSheet(sheet)
          setView("main")
        }}
        initialValues={customSheet ?? undefined}
        selectedBadge={selectedBadge ?? null}
        previewGuests={previewGuests}
        event={currentEvent}
        selectedCount={guest ? 1 : (selectedCount || 0)}
      />
    )
  }

  return (
    <ModalForm
      onSubmit={onSubmit}
      onClose={onClose}
      title="Guest Badge"
      submitText={submitText}
      submitIsDisabled={
        !preferredBadgeId || hasNoBadges || !badgePrinting || isCustomSheetNotConfigured
      }
      loading={isDownloading}
      size="xl"
    >
      {() => (
        <VStack spacing={6} align="stretch" p={1} m={-1}>
          {hasNoBadges ? (
            <NoBadges />
          ) : (
            <VStack spacing={1} align="stretch">
              <Text fontSize="md">
                Generate and download printable PDF badges.
              </Text>
              <DocsLink url={DESKTOP_PDF_BADGE_PRINTING_ARTICLE_URL} fontWeight="semibold">
                Desktop & PDF Badge Printing Guide
              </DocsLink>
            </VStack>
          )}

          {!badgePrinting && (
            <UpgradeAlert
              text={(
                <>
                  <b>Available on Corporate plans and above</b> <br />
                  Upgrade to unlock branded badge templates with secure QR codes,
                  bulk PDF downloads, and direct printing from the Diobox iOS app.
                </>
              )}
              defaultSelectedPlan={SubscriptionType.Corporate}
            />
          )}

          {!hasNoBadges && (
            <>
              <FormControl>
                <FormLabel>
                  {guest ? "Template" : "Badge Template"}
                </FormLabel>
                <ChakraSelect
                  value={preferredBadgeId}
                  onChange={(e) => setPreferredBadgeId(e.target.value)}
                >
                  {badges.map((badge) => (
                    <option key={badge.id} value={badge.id}>
                      {badge.name}
                    </option>
                  ))}
                </ChakraSelect>
              </FormControl>

              <SheetFormatSelector
                selectedFormat={selectedExportFormat}
                onFormatChange={(format) => {
                  setSelectedExportFormat(format)
                  setStartPosition(1)
                }}
                customSheet={customSheet}
                onCustomSheetConfigure={() => setView("customSheet")}
                selectedBadge={selectedBadge ?? null}
                previewGuests={previewGuests}
                event={currentEvent}
                selectedCount={guest ? 1 : (selectedCount || 0)}
                startPosition={startPosition}
              />

              {shouldShowStartPosition && (
                <FormControl>
                  <FormLabel>Start Position</FormLabel>
                  <ChakraSelect
                    maxW="100px"
                    value={startPosition}
                    onChange={(e) => setStartPosition(Number(e.target.value))}
                  >
                    {Array.from({ length: totalLabels }, (_, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </ChakraSelect>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Resolution (DPI)</FormLabel>
                <ChakraSelect
                  maxW="100px"
                  value={dpi}
                  onChange={(e) => setDpi(Number(e.target.value))}
                >
                  {DPI_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </ChakraSelect>
              </FormControl>
            </>
          )}

          {!guest && !hasNoBadges && (
            <Text fontSize="sm" color="gray.600">
              {isSheetMode
                ? "A PDF file with badges arranged on sheets"
                : "A PDF file containing individual badge pages"}{" "}
              will be generated and emailed to{" "}
              <Text as="strong" fontWeight="semibold">
                {currentUser.email}
              </Text>
              .
            </Text>
          )}
        </VStack>
      )}
    </ModalForm>
  )
}

export default PrintBadge
